import { msg } from '@lingui/macro'

import * as React from 'react'
import { Controller, useFormContext, UseFormProps } from 'react-hook-form'

import { LinguiMessageDescriptor, useTrans } from '~logic/i18n/useTrans'
import { AnimatedHeight } from '~ui/animatedHeight/AnimatedHeight'
import { InputControl } from '~ui/components/formElements/accessories/InputControl'
import { InputLabel } from '~ui/components/formElements/accessories/InputLabel'
import { InputErrorMessage } from '~ui/components/formElements/accessories/InputMessages'
import { Input } from '~ui/components/formElements/Input/Input'

import { FORM_INPUT_RADIUS } from '../config/form.config'

// BE validation pattern ported to ECMAScript’s regex implementation
const emailPattern = /^([\w+-].?)+@[a-z\d-]+(\.[a-z\d-]+)*\.[a-z]+$/

type TEmailForm = {
  email: string
}

type TProps = NoChildren & {
  label?: LinguiMessageDescriptor
  validationMode?: ExtractSafe<Defined<UseFormProps['mode']>, 'onChange' | 'onBlur'>
  isDisabled?: PropsOf<typeof Input>['isDisabled']
  isRequired?: PropsOf<typeof Input>['isRequired']
}

/**
 * Customer’s email input.
 *
 * Different labels can be used via the `label` prop, defaults to _Email address_ (translated).
 *
 * ! Needs the `FormProvider` from 'react-hook-form' to be rendered somewhere above,
 * see {@link https://react-hook-form.com/api/formprovider} for examples and details.
 */
export const EmailInput: React.FC<TProps> = ({
  label = msg`Email address`,
  validationMode,
  isDisabled = false,
  isRequired = true,
}) => {
  const { trans } = useTrans()
  const { control, trigger } = useFormContext<TEmailForm>()

  const triggerField = React.useCallback(() => {
    return trigger(`email`)
  }, [trigger])

  const triggerRules = {
    onChange: validationMode === `onChange` ? triggerField : undefined,
    onBlur: validationMode === `onBlur` ? triggerField : undefined,
  }

  // `isDisabled` is only used to display the disabled *filled* input from a previous step (for now)
  const shouldShowRequiredIndicator = !isDisabled && isRequired

  return (
    <InputControl isRequired={shouldShowRequiredIndicator}>
      <InputLabel>{trans(label)}</InputLabel>
      <Controller
        name={`email`}
        control={control}
        rules={{
          ...triggerRules,
          required: {
            value: isRequired,
            message: trans(msg`Fill the email address`),
          },
          pattern: {
            value: emailPattern,
            message: trans(msg`Invalid email`),
          },
        }}
        render={({
          field: { onChange, onBlur, value, name },
          fieldState: { invalid, error },
          formState: { isSubmitting },
        }) => (
          <>
            <Input
              key={isDisabled ? 0 : 1} // workaround to replace component and get rid of autocomplete background color that Chrome adds and that remains even on disabled input
              id={name}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
              isInvalid={invalid}
              inputMode={`email`}
              autoComplete={`email`}
              isDisabled={isSubmitting || isDisabled}
              radius={FORM_INPUT_RADIUS}
              placeholder={trans(msg`john.doe@gmail.com`)}
            />
            <AnimatedHeight isOpen={invalid && Boolean(error?.message)}>
              <InputErrorMessage>{error?.message ?? null}</InputErrorMessage>
            </AnimatedHeight>
          </>
        )}
      />
    </InputControl>
  )
}
